import { Link } from "react-router-dom";
import {
  Heart,
  Mail,
  Phone,
  MapPin,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  ExternalLink,
} from "lucide-react";

const Footer = () => {
  return (
    <footer className="bg-gradient-to-br from-foreground via-foreground/95 to-foreground/90 text-background relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10"></div>
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundRepeat: "repeat",
          }}
        ></div>
      </div>

      <div className="container mx-auto px-4 py-12 md:py-16 relative z-10">
        <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-6 md:gap-8 mb-8 md:mb-12">
          {/* Company Info */}
          <div className="space-y-4 md:space-y-6">
            <div>
              <h3 className="text-xl md:text-2xl font-bold mb-3 md:mb-4 bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">
                Kente Weave Design
              </h3>
              <p className="text-sm md:text-base text-background/80 leading-relaxed">
                Celebrating African heritage through authentic Kente sash
                designs. Create personalized graduation stoles that honor
                tradition while embracing your unique journey.
              </p>
            </div>

            {/* Orcta Technologies Badge */}
            <div className="bg-background/10 backdrop-blur-sm rounded-lg p-4 border border-background/20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
                  <ExternalLink className="h-5 w-5 text-background" />
                </div>
                <div>
                  <p className="text-sm text-background/70">Built by</p>
                  <p className="font-semibold text-background">
                    Orcta Technologies
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4 md:space-y-6">
            <h4 className="text-base md:text-lg font-semibold">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/designer"
                  className="text-background/80 hover:text-background transition-colors flex items-center gap-2"
                >
                  <span>Start Designing</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/gallery"
                  className="text-background/80 hover:text-background transition-colors flex items-center gap-2"
                >
                  <span>Pattern Gallery</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className="text-background/80 hover:text-background transition-colors flex items-center gap-2"
                >
                  <span>About Kente</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-background/80 hover:text-background transition-colors flex items-center gap-2"
                >
                  <span>Contact Us</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div className="space-y-4 md:space-y-6">
            <h4 className="text-base md:text-lg font-semibold">Support</h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/help"
                  className="text-background/80 hover:text-background transition-colors flex items-center gap-2"
                >
                  <span>Help Center</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/shipping"
                  className="text-background/80 hover:text-background transition-colors flex items-center gap-2"
                >
                  <span>Shipping Info</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/returns"
                  className="text-background/80 hover:text-background transition-colors flex items-center gap-2"
                >
                  <span>Returns</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/faq"
                  className="text-background/80 hover:text-background transition-colors flex items-center gap-2"
                >
                  <span>FAQ</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-4 md:space-y-6">
            <h4 className="text-base md:text-lg font-semibold">Get in Touch</h4>
            <div className="space-y-3 md:space-y-4">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 md:h-5 md:w-5 text-primary" />
                <span className="text-sm md:text-base text-background/80">
                  info@orctatech.com
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 md:h-5 md:w-5 text-primary" />
                <span className="text-sm md:text-base text-background/80">
                  +2330593939220
                </span>
              </div>
            </div>

            {/* Social Links */}
            <div className="pt-4">
              <h5 className="text-sm font-medium mb-3 text-background/70">
                Follow Us
              </h5>
              <div className="flex gap-3">
                <a
                  href="#"
                  className="w-10 h-10 bg-background/10 hover:bg-background/20 rounded-lg flex items-center justify-center transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="h-5 w-5" />
                </a>
                <a
                  href="#"
                  className="w-10 h-10 bg-background/10 hover:bg-background/20 rounded-lg flex items-center justify-center transition-colors"
                  aria-label="Twitter"
                >
                  <Twitter className="h-5 w-5" />
                </a>
                <a
                  href="#"
                  className="w-10 h-10 bg-background/10 hover:bg-background/20 rounded-lg flex items-center justify-center transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="h-5 w-5" />
                </a>
                <a
                  href="#"
                  className="w-10 h-10 bg-background/10 hover:bg-background/20 rounded-lg flex items-center justify-center transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-background/20 pt-8">
          <div className="flex flex-col gap-4 text-center md:text-left">
            {/* Copyright */}
            <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-2 text-background/70">
              <span className="text-sm sm:text-base">
                © 2025 Kente Weave Design. All rights reserved.
              </span>
            </div>

            {/* Acknowledgment */}
            <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-2 text-background/60">
              <span className="text-xs sm:text-sm">
                In honor of Prof Kente - preserving the legacy of authentic
                Kente craftsmanship
              </span>
            </div>

            {/* Legal Links */}
            <div className="flex flex-wrap justify-center md:justify-start gap-4 md:gap-6 text-sm">
              <Link
                to="/privacy"
                className="text-background/70 hover:text-background transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-background/70 hover:text-background transition-colors"
              >
                Terms of Service
              </Link>
              <Link
                to="/cookies"
                className="text-background/70 hover:text-background transition-colors"
              >
                Cookie Policy
              </Link>
              <Link
                to="/admin"
                className="text-background/50 hover:text-background/70 transition-colors text-xs"
              >
                Admin
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-primary/20 to-transparent rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-40 h-40 bg-gradient-to-tl from-secondary/20 to-transparent rounded-full blur-3xl"></div>
    </footer>
  );
};

export default Footer;
